import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/db70cssjfl1ikpv8spmg";

export default function FeedbackForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        body.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { Accept: "application/json" },
        body,
      });
      const responseText = await response.text();

      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请检查网络后重试。");
    }
  };

  const fieldClass =
    "w-full rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200";

  return (
    <form
      id="feedback-form"
      data-readdy-form
      action={FORM_URL}
      method="post"
      onSubmit={handleSubmit}
      className="rounded-2xl border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h3 className="text-lg font-bold text-foreground-950">给我们留言</h3>
      <p className="mt-1.5 text-sm text-foreground-600">
        对网站内容有任何建议，或者想分享你收藏的小黄人好物，都欢迎告诉我们。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-foreground-600">你的称呼</span>
          <input name="name" type="text" required placeholder="请输入你的昵称" className={fieldClass} />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-foreground-600">邮箱</span>
          <input name="email" type="email" required placeholder="用于接收回复" className={fieldClass} />
        </label>
      </div>

      <label className="mt-4 block">
        <span className="mb-1.5 block text-xs font-medium text-foreground-600">反馈类型</span>
        <select name="topic" className={fieldClass} defaultValue="内容建议">
          <option value="内容建议">内容建议</option>
          <option value="纠错反馈">纠错反馈</option>
          <option value="合作咨询">合作咨询</option>
          <option value="其他">其他</option>
        </select>
      </label>

      <label className="mt-4 block">
        <span className="mb-1.5 block text-xs font-medium text-foreground-600">
          留言内容（最多 500 字）
        </span>
        <textarea
          name="message"
          rows={5}
          maxLength={500}
          required
          placeholder="写下你想说的话…"
          className={`${fieldClass} resize-none`}
        ></textarea>
      </label>

      <input
        className="contact-extra-note"
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
          {status === "submitting" ? "提交中…" : "提交留言"}
        </button>

        {status === "success" && (
          <span className="flex items-center gap-1.5 text-sm font-medium text-primary-700">
            <i className="ri-checkbox-circle-fill"></i>
            提交成功，感谢你的留言！
          </span>
        )}
        {status === "error" && (
          <span className="flex items-center gap-1.5 text-sm font-medium text-secondary-700">
            <i className="ri-error-warning-fill"></i>
            {formError || "提交失败，请稍后重试。"}
          </span>
        )}
      </div>
    </form>
  );
}